/**
 * Wrapper de fetch que centraliza el manejo de 401 → redirect a /login.
 * NO se usa Authorization header: la sesión va por cookie HTTPOnly (DF6-03).
 * `credentials: "include"` asegura que el navegador envíe la cookie.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface FetchOptions {
  method?: Method;
  body?: unknown;
  signal?: AbortSignal;
}

let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

export async function api<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  const { method = "GET", body, signal } = opts;
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(path, {
    method,
    headers,
    credentials: "include",
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (response.status === 401 && onUnauthorized) {
    onUnauthorized();
  }

  // 204 No Content → sin body.
  if (response.status === 204) {
    return undefined as T;
  }

  let parsed: unknown = null;
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    parsed = await response.json();
  } else {
    parsed = await response.text();
  }

  if (!response.ok) {
    const errorBody =
      parsed && typeof parsed === "object" && "error" in parsed
        ? (parsed as { error: { code: string; message: string } }).error
        : { code: "http_error", message: String(parsed ?? response.statusText) };
    throw new ApiError(response.status, errorBody.code, errorBody.message, parsed);
  }

  return parsed as T;
}
